type EmoticonRule = {
  pattern: RegExp
  replacement: string
}

// ASCII hyphen and common dash/minus characters from paste or OS autocorrect.
const DASH = '-\\u2010\\u2011\\u2012\\u2013\\u2014\\u2212'

const EMOTICON_RULES: EmoticonRule[] = [
  { pattern: /:-\)$|:\)$|=\)$/, replacement: '🙂' },
  { pattern: /:-\($|:\($|=\($/, replacement: '😢' },
  { pattern: /:-[Pp]$|:[Pp]$/, replacement: '😛' },
  { pattern: /;-\)$|;\)$/, replacement: '😉' },
  { pattern: /:-\|$/, replacement: '😐' },
  { pattern: /:-\/$|:\/$|:-\\$/, replacement: '🫤' },
  { pattern: /:-[Dd]$|:[Dd]$/, replacement: '😀' },
  { pattern: new RegExp(`8[${DASH}][Dd]$|8[Dd]$`), replacement: '😎' },
  { pattern: new RegExp(`8[${DASH}][Oo]$|8[Oo]$`), replacement: '😮' },
]

/** Letter / digit / underscore — emoticon must not sit inside a word (e.g. `http://`). */
const WORD_CHAR = /[\p{L}\p{N}_]/u

const isStandaloneEmoticon = (beforeCaret: string, matchLength: number): boolean => {
  const matchStart = beforeCaret.length - matchLength
  if (matchStart <= 0) return true

  return !WORD_CHAR.test(beforeCaret.charAt(matchStart - 1))
}

const getTextBeforeCaret = (editor: HTMLElement): string | undefined => {
  const selection = window.getSelection()
  if (!selection?.rangeCount || !selection.isCollapsed) return

  const range = selection.getRangeAt(0)
  if (!editor.contains(range.commonAncestorContainer)) return

  const preRange = document.createRange()
  preRange.selectNodeContents(editor)
  preRange.setEnd(range.startContainer, range.startOffset)

  return preRange.toString()
}

const createRangeForMatchEndingAtCaret = (
  editor: HTMLElement,
  endRange: Range,
  matchLength: number,
): Range | undefined => {
  const preRange = document.createRange()
  preRange.selectNodeContents(editor)
  preRange.setEnd(endRange.startContainer, endRange.startOffset)

  const beforeCaret = preRange.toString()
  if (beforeCaret.length < matchLength) return

  const startFlatOffset = beforeCaret.length - matchLength
  const matchRange = document.createRange()
  matchRange.setEnd(endRange.startContainer, endRange.startOffset)

  const walker = document.createTreeWalker(editor, NodeFilter.SHOW_TEXT)
  let traversed = 0
  let node = walker.nextNode()

  while (node) {
    const textNode = node as Text
    const length = textNode.length
    const nodeStart = traversed
    const nodeEnd = traversed + length

    if (startFlatOffset >= nodeStart && startFlatOffset <= nodeEnd) {
      matchRange.setStart(textNode, startFlatOffset - nodeStart)
      return matchRange
    }

    traversed += length
    node = walker.nextNode()
  }

  return
}

export const applyTextEmoticons = (editor: HTMLElement | undefined): boolean => {
  if (!editor) return false

  const selection = window.getSelection()
  if (!selection?.rangeCount || !selection.isCollapsed) return false

  const caretRange = selection.getRangeAt(0)
  if (!editor.contains(caretRange.commonAncestorContainer)) return false

  const beforeCaret = getTextBeforeCaret(editor)
  if (!beforeCaret) return false

  const rule = EMOTICON_RULES.find(({ pattern }) => pattern.test(beforeCaret))
  if (!rule) return false

  const match = beforeCaret.match(rule.pattern)
  if (!match) return false
  if (!isStandaloneEmoticon(beforeCaret, match[0].length)) return false

  const matchRange = createRangeForMatchEndingAtCaret(editor, caretRange, match[0].length)
  if (!matchRange) return false

  matchRange.deleteContents()
  const emojiNode = document.createTextNode(rule.replacement)
  matchRange.insertNode(emojiNode)

  const range = document.createRange()
  range.setStartAfter(emojiNode)
  range.collapse(true)
  selection.removeAllRanges()
  selection.addRange(range)

  return true
}
